// src/pages/Usuario/Perfil.tsx
//
// Fidelidade ao artboard `portal-perfil.dc.html` (UI-2).
//
// Composição do canvas: `h1` 24/800 + sublinha de 13px e, abaixo, DOIS cards LADO
// A LADO (esquerda `flex:1`, direita `flex:0 0 330px`), não empilhados. O da
// esquerda abre com avatar de 48px + título 15/800 e lista os dados em `.kv`
// (linha `space-between` de 12px com régua tracejada); o da direita é a troca de
// senha, com rótulo externo `.flabel` sobre campo `.fld` (§6.3) e o `.btnpri` de
// largura total.
//
// Contratos do backend (apps/backend-portal/src/usuario/routes.ts):
//   GET  /usuario/perfil        → { usuario: { usr_codigo, usr_login, usr_nome, usr_email } }
//   POST /usuario/trocar-senha  { senha_atual, senha_nova }
//
// TODO (perfil-completo SN): o Perfil do SN também tem Endereço, Contato e
// Notificações. Exige mapear persona/fr_usuario ao vivo e ampliar o GET. Ver
// SPEC-PORTAL-SN-TOMADOR-PF.md §10.

import { z } from 'zod';
import useSWR from 'swr';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';

import { fetcher } from 'src/lib/axios';
import { api } from 'src/auditoria/infra/http/api-client';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxForm } from 'src/auditoria/components/tx-form/tx-form';

import { Field } from 'src/components/hook-form';

import { DsNum, DsField, DsButton, DsPageHeader } from 'src/auditoria/components/ds';

// ----------------------------------------------------------------------

interface Usuario {
  usr_codigo: number;
  usr_login: string;
  usr_nome: string | null;
  usr_email: string | null;
  // O canvas lista Inscrição municipal e Situação; o GET ainda não os devolve —
  // opcionais com fallback '—' até o backend ampliar.
  inscricao_municipal?: string | null;
  situacao?: string | null;
}

interface PerfilResponse {
  usuario: Usuario;
}

// ----------------------------------------------------------------------

/** `.kv`: 12px, `space-between`, 7px de respiro e régua tracejada (a última sem). */
function LinhaKv({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '10px',
        padding: '7px 0',
        fontSize: 12,
        borderBottom: '1px dashed',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {label}
      </Box>
      <Box component="b" sx={{ minWidth: 0, textAlign: 'right' }}>
        {value}
      </Box>
    </Box>
  );
}

/** Iniciais do contribuinte para o `.avatar` — duas letras, como no canvas. */
function iniciais(nome: string | null | undefined): string {
  const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '—';
  return (partes[0][0] + (partes[1]?.[0] ?? '')).toUpperCase();
}

function textoOuTraco(valor: React.ReactNode): React.ReactNode {
  return valor === null || valor === undefined || valor === '' ? '—' : valor;
}

// ----------------------------------------------------------------------

function DadosContribuinte() {
  const { data, isLoading } = useSWR<PerfilResponse>('/portal-api/usuario/perfil', fetcher);
  const u = data?.usuario;

  return (
    <Card sx={{ flex: 1, minWidth: 0, padding: '18px 20px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '12px' }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            flex: '0 0 48px',
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'background.neutral',
            color: 'text.secondary',
            fontSize: 15,
            fontWeight: 800,
          }}
        >
          {iniciais(u?.usr_nome)}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: 15, fontWeight: 800 }}>Meu Perfil</Box>
          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
            Cadastro do contribuinte na Secretaria de Finanças
          </Box>
        </Box>
      </Box>

      {isLoading && <Box sx={{ fontSize: 12, color: 'text.secondary' }}>Carregando...</Box>}

      {u && (
        <>
          <LinhaKv label="Código" value={<DsNum>{u.usr_codigo}</DsNum>} />
          <LinhaKv label="Login (CPF / CNPJ)" value={<DsNum>{u.usr_login}</DsNum>} />
          <LinhaKv label="Nome" value={textoOuTraco(u.usr_nome)} />
          <LinhaKv label="E-mail" value={textoOuTraco(u.usr_email)} />
          <LinhaKv
            label="Inscrição municipal"
            value={u.inscricao_municipal ? <DsNum>{u.inscricao_municipal}</DsNum> : '—'}
          />
          <LinhaKv label="Situação" value={textoOuTraco(u.situacao)} />
        </>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------

const SENHA_MIN = 8;

const schema = z
  .object({
    senha_atual: z.string().min(1, 'Informe a senha atual'),
    senha_nova: z.string().min(SENHA_MIN, `A nova senha deve ter ao menos ${SENHA_MIN} caracteres`),
    confirmar: z.string().min(1, 'Confirme a nova senha'),
  })
  .refine((d) => d.senha_nova === d.confirmar, {
    message: 'A confirmação não confere com a nova senha',
    path: ['confirmar'],
  });

type AlterarSenhaForm = z.infer<typeof schema>;

/** `.ferr` (§6.3): 11px/600 sem o recuo lateral do `FormHelperText`. */
const SX_CAMPO_SENHA = {
  mb: '10px',
  '& .MuiFormHelperText-root': {
    fontSize: 11,
    fontWeight: 600,
    margin: '6px 0 0',
  },
};

function Seguranca() {
  const methods = useForm<AlterarSenhaForm>({
    resolver: zodResolver(schema),
    defaultValues: { senha_atual: '', senha_nova: '', confirmar: '' },
  });

  const onSubmit = async (d: AlterarSenhaForm) => {
    await api.post('/portal-api/usuario/trocar-senha', {
      senha_atual: d.senha_atual,
      senha_nova: d.senha_nova,
    });
    toast.success('Senha alterada com sucesso');
    methods.reset();
  };

  return (
    <Card sx={{ flex: { xs: '1 1 auto', md: '0 0 330px' }, padding: '18px 20px' }}>
      <Box sx={{ fontSize: 13, fontWeight: 800, mb: '10px' }}>Alterar Senha</Box>

      <TxForm<AlterarSenhaForm> methods={methods} onSubmit={onSubmit}>
        <DsField label="Senha atual" density="sm" sx={SX_CAMPO_SENHA}>
          <Field.Text name="senha_atual" type="password" autoComplete="current-password" />
        </DsField>

        <DsField label="Nova senha" density="sm" sx={SX_CAMPO_SENHA}>
          <Field.Text name="senha_nova" type="password" autoComplete="new-password" />
        </DsField>

        <DsField label="Confirmar nova senha" density="sm" sx={SX_CAMPO_SENHA}>
          <Field.Text name="confirmar" type="password" autoComplete="new-password" />
        </DsField>

        <DsButton type="submit" size="tall" sx={{ width: '100%', justifyContent: 'center' }}>
          Atualizar senha
        </DsButton>

        <Box sx={{ fontSize: 11, color: 'text.secondary', mt: '10px' }}>
          Mínimo {SENHA_MIN} caracteres · letras e números · a sessão continua ativa neste
          dispositivo.
        </Box>
      </TxForm>
    </Card>
  );
}

// ----------------------------------------------------------------------

export default function PerfilPage() {
  return (
    <DashboardContent>
      <DsPageHeader
        title="Meu Perfil"
        weight={800}
        subtitle="Dados cadastrais do contribuinte e troca de senha."
      />

      {/* Os 330px da coluna direita são fixos no canvas; abaixo de `md` as duas
          colunas empilham — comportamento inventado, registrado no mailbox. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-start' },
          gap: '16px',
        }}
      >
        <DadosContribuinte />
        <Seguranca />
      </Box>
    </DashboardContent>
  );
}
